// Geometry for the crafting panel. Like the chest/trade screen, the crafting screen now shows the
// player's backpack underneath its own UI -- the materials every recipe draws from. Both halves live
// in ONE docked panel that has to stay inside the 442px-tall screen, so neither half can claim a
// fixed size: the recipe rows compress (down to a floor) to make room for the backpack, and the
// backpack picks the largest slot size whose rows still fit in whatever is left.
//
// Pure math, no pixi -- see __tests__/crafting_layout.test.ts.

// Slot sizes the backpack grid will try, largest first. Each one implies its own column count,
// fitted to the panel's inner width (32 -> 8 cols, 28 -> 9, 24 -> 10 at a 4px gap in 284px).
export const BACKPACK_SLOT_SIZES = [32, 28, 24]
export const GRID_GAP = 4
// Chests/players always show at least two rows on the trade screen; match that so a light backpack
// still reads as a grid rather than a lone strip of icons.
const MIN_ROWS = 2
// Rows the recipe list is asked to give up room for. A fuller backpack than this shrinks its own
// slots rather than squeezing the recipes further.
const MAX_RESERVED_ROWS = 3

export const RECIPE_GAP = 4
export const RECIPE_CARD_MAX_H = 52
// Below this a recipe card can't hold its two lines (name + ingredients / missing-materials reason).
export const RECIPE_CARD_MIN_H = 36
// Height of the "No recipes available yet." placeholder that stands in for the rows.
const RECIPE_EMPTY_H = 24

// Gap between the recipe list and the backpack's section label, and from that label to its slots.
// Both are kept tight on purpose: a handful of pixels here is the difference between the pack fitting
// a third row of slots and having to report the tail of itself as "+N more".
const SECTION_GAP = 6
const LABEL_H = 18

export interface BackpackGrid {
    slot: number
    cols: number
    rows: number
    gap: number
    height: number
    // Items with no cell in the grid. Only ever non-zero in the corner where a full recipe page has
    // already eaten the panel; the screen says "+N more" rather than swallowing them silently.
    hidden: number
}

export interface CraftingLayout {
    // Height of each recipe card (0 when there are no recipes to draw).
    cardH: number
    // First y below the recipe list.
    recipesBottom: number
    labelY: number
    gridY: number
    grid: BackpackGrid
    // Bottom of the laid-out content. Past `bottomY` only in the extreme case where even the
    // smallest backpack grid overruns what the recipes left behind.
    bottom: number
}

const rowsHeight = (slot: number, rows: number): number => rows * (slot + GRID_GAP) - GRID_GAP

// What one slot size can make of `maxH`: every row it needs when they fit (padded out to MIN_ROWS if
// there is room to spare), otherwise every row it can draw.
const gridFor = (slot: number, itemCount: number, innerW: number, maxH: number): BackpackGrid => {
    const cols = Math.max(1, Math.floor((innerW + GRID_GAP) / (slot + GRID_GAP)))
    const needed = Math.max(1, Math.ceil(itemCount / cols))
    const affordable = Math.floor((maxH + GRID_GAP) / (slot + GRID_GAP))
    const rows = needed <= affordable ? Math.min(affordable, Math.max(needed, MIN_ROWS)) : Math.max(1, affordable)
    return {
        slot,
        cols,
        rows,
        gap: GRID_GAP,
        height: rowsHeight(slot, rows),
        hidden: Math.max(0, itemCount - rows * cols),
    }
}

// Preference order: the roomiest slots that still give every item a cell; failing that (a full recipe
// page has taken the panel), whichever size shows the most, so the panel stays on screen and says
// "+N more" rather than running off the bottom edge.
export const backpackGrid = (itemCount: number, innerW: number, maxH: number): BackpackGrid => {
    const candidates = BACKPACK_SLOT_SIZES.map(slot => gridFor(slot, itemCount, innerW, maxH))
    const complete = candidates.find(c => c.height <= maxH && c.hidden === 0)
    if (complete) {
        return complete
    }

    const fitting = candidates.filter(c => c.height <= maxH)
    // Ties keep the earlier (roomier) candidate.
    return (fitting.length > 0 ? fitting : candidates).reduce((best, c) => (c.hidden < best.hidden ? c : best))
}

export const craftingLayout = ({
    topY,
    bottomY,
    innerW,
    recipeCount,
    itemCount,
}: {
    // First y the recipe list may draw at (below the tabs and paginator).
    topY: number
    // Last y the panel may draw at.
    bottomY: number
    // Panel width inside its margins.
    innerW: number
    recipeCount: number
    itemCount: number
}): CraftingLayout => {
    // Reserve what the backpack wants at the roomiest slot size given unlimited height, capped so a
    // full pack can't starve the recipe list -- past the cap the grid compensates with smaller slots.
    const preferred = gridFor(BACKPACK_SLOT_SIZES[0], itemCount, innerW, Number.MAX_SAFE_INTEGER)
    const cappedRows = Math.min(preferred.rows, MAX_RESERVED_ROWS)
    const reserved = SECTION_GAP + LABEL_H + rowsHeight(preferred.slot, cappedRows)

    const forRecipes = bottomY - topY - reserved
    const cardH =
        recipeCount > 0
            ? Math.max(
                  RECIPE_CARD_MIN_H,
                  Math.min(RECIPE_CARD_MAX_H, Math.floor((forRecipes + RECIPE_GAP) / recipeCount) - RECIPE_GAP),
              )
            : 0

    const recipesBottom =
        recipeCount > 0 ? topY + recipeCount * (cardH + RECIPE_GAP) - RECIPE_GAP : topY + RECIPE_EMPTY_H

    const labelY = recipesBottom + SECTION_GAP
    const gridY = labelY + LABEL_H
    const grid = backpackGrid(itemCount, innerW, bottomY - gridY)

    return { cardH, recipesBottom, labelY, gridY, grid, bottom: gridY + grid.height }
}
